<template>
  <div
    itemscope
    itemprop="review"
    itemtype="https://schema.org/Review"
    :id="cancellationReview.id"
    :aria-label="cancellationReview.isTopReview && index === 0 ? $t('messages_nl.cancellation.cancellationReview.topReview') : null"
    :class="[
      'relative box-theme box-theme--primary box-theme--position-center w-full h-full flex flex-col p-4',
      isHashTagged ? 'shadow-2xl font-semibold border-2 border-slate-600 scale-105 !bg-highlighted-review text-white z-10' : '',
      cancellationReview.isTopReview && index === 0 ? 'outline-[6px] rounded-xl bg-fontcolor-dark before:block before:absolute before:z-min-1 before:w-[calc(100%_+_1rem)] before:h-[calc(100%_+_3rem)] before:pt-2 before:pl-6 before:-left-2 before:-top-10 before:bg-fontcolor-dark before:rounded-[1.25rem] before:text-white before:content-[attr(aria-label)]' : 'box-theme--shadow-against-white-background box-theme--border-against-white-background',
    ]"
  >
    <header class="flex gap-2 flex-col md:flex-row justify-between pb-3 border-b-1 border-gray">
      <div class="flex flex-col gap-1">

        <!-- reviewer name -->
        <div class="flex items-start">
          <img
            :src="$asset(`img/common/user${hashTagged}.svg?v1`)"
            width="19"
            height="19"
            class="mt-1 mr-2"
            loading="lazy"
            :alt="$t('messages_nl.cancellation.cancellationReview.author') + ':'"
          />
          <span
            class="text-lg font-bold"
            itemprop="author"
          >
            {{ cancellationReview.name }}
          </span>
        </div>

        <!-- location -->
        <div class="flex">
          <img
            :src="$asset(`img/common/pointer${hashTagged}.svg`)"
            width="18"
            height="26"
            class="-mt-0.5 mr-2 text-fontcolor-dark"
            loading="lazy"
            :alt="$t('messages_nl.cancellation.cancellationReview.location') + ':'"
          />
          <span itemprop="location" class="text-sm">{{ cancellationReview.city }}</span>
        </div>
      </div>

      <!-- date -->
      <time
        pubdate
        :datetime="cancellationReview.updatedAt.substring(0,10)"
        class="text-sm"
        itemprop="datePublished"
      >
        {{ updatedAt }}
      </time>
    </header>
    <main class="py-4">

      <!-- rating -->
      <div
        itemscope
        itemprop="reviewRating"
        itemtype="https://schema.org/Rating"
      >
        <meta
          itemprop="worstRating"
          content="1"
        >
        <meta
          itemprop="ratingValue"
          :content="cancellationReview.rating"
        >
        <meta
          itemprop="bestRating"
          content="5"
        >
        <div
          class="rating rating--size-md bg-white pt-1 pb-2 rounded-full"
          role="img"
          :aria-label="$t(`messages_nl.cancellation.cancellationReview.rating`, { rating: cancellationReview.rating })"
        >
          <img
            v-for="star in 5"
            :key="star"
            :src="$asset(`img/common/stars/${starColor}/${star <= cancellationReview.rating ? 'full' : 'empty'}-star.svg`)"
            class="box-content filter-grey-outline"
            width="22"
            height="22"
            loading="lazy"
            aria-hidden="true"
            alt=""
          />
        </div>
      </div>

      <!-- description -->
      <span
        class="block pt-4 text-left"
        itemprop="reviewBody"
        v-html="cancellationReview.description"
      />
    </main>

    <!-- bottom buttons -->
    <footer class="flex items-center justify-between h-10 border-t-1 pt-3">
      <div class="flex items-center gap-4 bg-white pr-4 rounded-full z-10">

        <!-- like button -->
        <button
          class="inline-flex items-center cursor-pointer hover:scale-110 active:scale-95 transition-transform"
          @click="likeButton()"
          :aria-labelledby="'like-label-' + cancellationReview.id"
        >
          <div class="h-8 flex items-center" role="img" :aria-label="likes + ' ' + $t(`messages_nl.cancellation.cancellationReview.like.like${likes > 1 ? 's' : ''}`)">
            <img
              :src="$asset(`img/common/${hasLike ? 'thumbs-up-green' : 'thumbs-up'}${hashTagged}.svg`)"
              class="mr-1"
              width="18"
              height="18"
              loading="lazy"
              aria-hidden="true"
              alt=""
            />
            <div
              :class="[
              'relative -left-2 text-xs px-1 rounded-full border-fontcolor-default flex items-center font-bold mt-6',
              isHashTagged ? 'bg-white text-fontcolor-default' : 'bg-fontcolor-default text-white',
            ]"
            >
              {{ likes }}
            </div>
          </div>
          <p :id="'like-label-' + cancellationReview.id" class="text-sm -ml-1 flex items-center">
            {{ $t('messages_nl.cancellation.cancellationReview.like.text') }}<span class="sr-only"> ({{ likes }} {{ $t(`messages_nl.cancellation.cancellationReview.like.like${likes > 1 ? 's' : ''}`) }})</span>
          </p>
        </button>

        <!-- share button -->
        <button
          class="inline-flex gap-2 items-center cursor-pointer hover:scale-110 active:scale-95 transition-transform"
          @click="toggleShowShareWidget()"
          :aria-labelledby="'share-label-' + cancellationReview.id"
        >
          <img
            :src="$asset(`img/common/share${hashTagged}.svg`)"
            width="18"
            height="18"
            loading="lazy"
            aria-hidden="true"
            alt=""
          />
          <p :id="'share-label-' + cancellationReview.id" class="text-sm flex items-center">
            {{ $t('messages_nl.global.share') }}
          </p>
        </button>
      </div>

      <transition
          enter-from-class="-translate-x-[150%] opacity-0"
          leave-to-class="-translate-x-[150%] opacity-0"
          enter-active-class="transition duration-300"
          leave-active-class="transition duration-300"
      >
        <adivare-share-widget
          size="xs"
          v-show="showShareWidget"
          :companyName="companyName"
          :cancellationReview="cancellationReview"
          :isSingleReview="isSingleReview"
          :aria-hidden="!showShareWidget"
        />
      </transition>
    </footer>
  </div>
</template>

<script>
import AdivareShareWidget from '../share-widget.vue';
import { createDateFormatter } from '../../../translations/date-formatter';
import axios from 'axios';

export default {
  name: "adivare-cancellation-review",
  components: {
    AdivareShareWidget,
  },
  props: [
    'cancellationReview',
    'isSingleReview',
    'companyName',
    'index',
  ],
  data() {
    return {
      dateLocale: this.$i18n.locale,
      likes: 0,
      hasLike: false,
      updatedAt: '',
      showShareWidget: false,
      isHashTagged: false,
      starColors: {
        '1': 'red',
        '1.5': 'red',
        '2': 'orange',
        '2.5': 'orange',
        '3': 'yellow',
        '3.5': 'yellow',
        '4': 'light-green',
        '4.5': 'green',
        '5': 'green',
      }
    }
  },
  computed: {
    hashTagged() {
      if (this.isHashTagged) {
        return '-white'
      } else {
        return ''
      }
    },
    starColor() {
      if (this.cancellationReview.rating in this.starColors) {
        return this.starColors[this.cancellationReview.rating];
      }

      return 'default';
    },
  },
  methods: {
    toggleShowShareWidget() {
      this.showShareWidget = !this.showShareWidget
    },
    likeButton() {
      if (this.hasLike === false) {
        axios.get(`/public/api/cancellation-review/${this.cancellationReview.id}/like`).then((response) => {
          this.hasLike = 'success' === response.data.status;
        });

        this.likes = this.likes + 1;
      }
    },
    formatDate(date, options = {
      dateStyle: 'long',
    }) {
      try {
        this.updatedAt = createDateFormatter(this.dateLocale).format(date, options);
      } catch (e) {
        if (!(date instanceof Date)) {
          console.error(`Unable to format the date: ${date}`);
          return 'string' === typeof date ? date : '';
        }
      }
    }
  },
  created() {
    this.formatDate(this.cancellationReview.updatedAt);
    this.likes = this.cancellationReview.likes;
    this.hasLike = this.cancellationReview.hasLike;
  },
  mounted() {
    this.isHashTagged = window.location.hash.substring(1) === this.cancellationReview.id;
  }
}
</script>
